import { CmsOffer, GiftListItem } from '../../types/cmsProduct';
import { RewardOffers } from './types';

/**
 * Pure presentation helpers for the Hot Reward Pick column (CMS-1687).
 *
 * These functions are deliberately date-free: expiry filtering happens upstream
 * in the fe-services mapper — both the product offers (`filterOffers`) and the
 * value-cell offer-selector (`rewardOffers` / cell gift titles) are filtered by
 * `isOfferActive` at map time before they reach the table, so the offer arrays
 * here are guaranteed active (published + in-period). That keeps the cell/module
 * presentational and SSR-safe (no `new Date()` in render or `useMemo`).
 *
 * The in-table cell titles are authored directly on the value cell (the gift
 * selector → `hotRewardGiftTitles`), so there is no offer-derivation step for
 * the cell. The expanded module's groups come from the curated `RewardOffers`,
 * but a value-cell title override still wins (see `getRewardModuleGroups`).
 */

export const MAX_HOT_REWARD_GIFTS = 2;

/**
 * Value-cell gift-title selector entry: the displayed `title` (override or the
 * gift's own title) plus `originalTitle` (the gift's own title), used to match
 * the same gift in the offers by title.
 */
export type GiftTitleOverride = {
  id: number;
  title: string;
  originalTitle?: string;
};

/**
 * Extract gift titles from an offer in Strapi list order, skipping items without
 * a title. A value-cell title override wins over the gift's own title, so the
 * module shows the same authored titles as the cell. Matching is by gift title:
 * the value-cell gift (SingleGift) and the offer gift (Gift) are different Strapi
 * content types with no shared id/uid, so the title is the only stable link.
 */
const giftTitlesOf = (
  offer: CmsOffer | undefined,
  titleOf: (item: GiftListItem) => string | undefined,
): string[] =>
  (offer?.list_of_gifts ?? [])
    .map(titleOf)
    .filter((title): title is string => !!title);

/**
 * The title a gift shows in the module: the value-cell override when one
 * changes it, else the gift's own.
 */
export const giftTitleWithOverrides = (
  giftTitleOverrides: GiftTitleOverride[] = [],
): ((item: GiftListItem) => string | undefined) => {
  // Only entries that actually change the title are kept, so a gift with no
  // override never shadows a different offer gift that happens to share its title.
  const overrideByTitle = new Map(
    giftTitleOverrides
      .filter(
        (g): g is GiftTitleOverride & { originalTitle: string } =>
          !!g.originalTitle && g.title !== g.originalTitle,
      )
      .map((g) => [g.originalTitle, g.title] as const),
  );
  return (item) => {
    const original = item.gift?.gift_title;
    return (original && overrideByTitle.get(original)) || original;
  };
};

export type RewardModuleTnc = {
  uid: string;
  description: string;
};

export type RewardModuleGroups = {
  exclusive: string[];
  welcome: string[];
  tnc: RewardModuleTnc[];
};

/**
 * Build the grouped content for the expanded "See the full reward" module from
 * the cell's curated reward offers (CMS-1687).
 *
 * - `exclusive` / `welcome`: all offers' gift titles (bulleted lists), with the
 *   value-cell title override applied per gift (matched by gift title). No
 *   welcome promo description (Req 5.4b).
 * - `tnc`: one entry per offer (exclusive then welcome) that has a description,
 *   each keyed by a stable uid (used as the React list key; the module renders a
 *   single Terms & Conditions accordion, Req 5.4c).
 */
export const getRewardModuleGroups = (
  rewardOffers: RewardOffers | undefined,
  giftTitleOverrides: GiftTitleOverride[] = [],
): RewardModuleGroups => {
  const titleOf = giftTitleWithOverrides(giftTitleOverrides);
  const exclusiveOffers = rewardOffers?.exclusiveOffers ?? [];
  const welcomeOffers = rewardOffers?.welcomeOffers ?? [];

  const exclusive = exclusiveOffers.flatMap((o) => giftTitlesOf(o, titleOf));
  const welcome = welcomeOffers.flatMap((o) => giftTitlesOf(o, titleOf));

  const tnc: RewardModuleTnc[] = [...exclusiveOffers, ...welcomeOffers]
    .filter((offer) => !!offer.description)
    .map((offer, index) => ({
      uid: offer.uid ?? `offer-${index}`,
      description: offer.description as string,
    }));

  return { exclusive, welcome, tnc };
};
